import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { brands } from '@/mocks/articles';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t } = useTranslation('common');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);
  const [isMobileGuidesOpen, setIsMobileGuidesOpen] = useState(false);
  const [isMobileBrandsOpen, setIsMobileBrandsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-foreground-950/20 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg transition-colors duration-300 ${isScrolled ? 'bg-primary-500' : 'bg-background-50/25'}`}>
              <span className={`font-bold text-sm md:text-base font-label transition-colors duration-300 ${isScrolled ? 'text-background-50' : 'text-background-50'}`}>K</span>
            </div>
            <span className={`font-heading font-semibold text-base md:text-lg whitespace-nowrap transition-colors duration-300 ${isScrolled ? 'text-foreground-950' : 'text-background-50'}`}>
              Kakobuy Spreadsheet
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 text-sm transition-colors duration-200 rounded-lg font-label whitespace-nowrap ${
                isScrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-background-50/85 hover:text-background-50 hover:bg-background-50/10'
              }`}
            >
              <i className="ri-home-4-line mr-1.5 text-sm"></i>{t('nav_home')}
            </Link>

            {/* Category Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm transition-colors duration-200 rounded-lg font-label whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                    : 'text-background-50/85 hover:text-background-50 hover:bg-background-50/10'
                }`}
              >
                {t('nav_categories')}
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-background-50 rounded-xl border border-background-200/80 overflow-hidden animate-[fadeIn_0.15s_ease-out] shadow-lg">
                  <div className="max-h-[420px] overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.seq}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors duration-150 whitespace-nowrap"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <div className="w-9 h-9 flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={cat.imageUrl}
                            alt={cat.name}
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                          />
                        </div>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/poradnik-zakupowy-kakobuy"
              className={`px-4 py-2 text-sm transition-colors duration-200 rounded-lg font-label whitespace-nowrap ${
                isScrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-background-50/85 hover:text-background-50 hover:bg-background-50/10'
              }`}
            >
              <i className="ri-shopping-bag-3-line mr-1.5 text-sm"></i>{t('nav_guides')}
            </Link>
            <Link
              to="/kakobuy-spreadsheet-2026"
              className={`px-4 py-2 text-sm transition-colors duration-200 rounded-lg font-label whitespace-nowrap ${
                isScrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-background-50/85 hover:text-background-50 hover:bg-background-50/10'
              }`}
            >
              <i className="ri-article-line mr-1.5 text-sm"></i>{t('nav_blog')}
            </Link>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-2">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 text-sm rounded-full transition-all duration-200 font-label font-medium whitespace-nowrap cursor-pointer ${
                isScrolled
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  : 'bg-background-50 text-foreground-950 hover:bg-background-50/90'
              }`}
            >
              {t('btn_buy_now')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer transition-colors duration-300 ${
              isScrolled ? 'text-foreground-950' : 'text-background-50'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 animate-[fadeIn_0.2s_ease-out] max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-0.5">
            {/* Home */}
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-800 font-medium rounded-xl hover:bg-background-100 font-label"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-home-4-line text-base text-primary-500"></i>
              </div>
              {t('nav_home')}
            </Link>

            {/* Shopping Guide */}
            <Link
              to="/poradnik-zakupowy-kakobuy"
              className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-800 font-medium rounded-xl hover:bg-background-100 font-label"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-shopping-bag-3-line text-base text-secondary-500"></i>
              </div>
              {t('nav_guides')}
            </Link>

            {/* Blog */}
            <Link
              to="/kakobuy-spreadsheet-2026"
              className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-800 font-medium rounded-xl hover:bg-background-100 font-label"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-article-line text-base text-accent-600"></i>
              </div>
              {t('nav_blog')}
            </Link>

            {/* Divider */}
            <div className="border-t border-background-200/70 my-2"></div>

            {/* Categories Collapsible */}
            <button
              onClick={() => setIsMobileCatOpen(!isMobileCatOpen)}
              className="flex items-center justify-between w-full px-4 py-3 text-xs text-foreground-400 font-label uppercase tracking-wider cursor-pointer"
            >
              <span>{t('nav_categories')}</span>
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isMobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {isMobileCatOpen && (
              <div className="space-y-0.5 pb-2">
                {categories.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-600 rounded-xl hover:bg-background-100 font-label"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={cat.imageUrl}
                        alt={cat.name}
                        className="w-full h-full object-cover object-top"
                        loading="lazy"
                      />
                    </div>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            {/* Brands Collapsible */}
            <button
              onClick={() => setIsMobileBrandsOpen(!isMobileBrandsOpen)}
              className="flex items-center justify-between w-full px-4 py-3 text-xs text-foreground-400 font-label uppercase tracking-wider cursor-pointer"
            >
              <span>Popularne marki</span>
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isMobileBrandsOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {isMobileBrandsOpen && (
              <div className="space-y-0.5 pb-2">
                {brands.map((brand) => (
                  <a
                    key={brand.seq}
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-600 rounded-xl hover:bg-background-100 font-label"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-foreground-500">{brand.name.charAt(0)}</span>
                    </div>
                    {brand.name}
                  </a>
                ))}
              </div>
            )}

            {/* Guides Collapsible */}
            <button
              onClick={() => setIsMobileGuidesOpen(!isMobileGuidesOpen)}
              className="flex items-center justify-between w-full px-4 py-3 text-xs text-foreground-400 font-label uppercase tracking-wider cursor-pointer"
            >
              <span>Poradniki</span>
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isMobileGuidesOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {isMobileGuidesOpen && (
              <div className="space-y-0.5 pb-2">
                {[
                  { label: 'Jak kupować przez agenta', slug: 'jak-kupowac-przez-agenta', icon: 'ri-guide-line' },
                  { label: 'Jak korzystać ze Spreadsheet', slug: 'czym-jest-kakobuy-spreadsheet', icon: 'ri-file-list-3-line' },
                  { label: 'Jak znaleźć sprzedawcę', slug: 'najlepsi-sprzedawcy-kakobuy', icon: 'ri-user-search-line' },
                  { label: 'QC Guide - Sprawdź jakość', slug: 'jak-sprawdzic-qc', icon: 'ri-check-double-line' },
                  { label: 'Shipping Guide - Wysyłka', slug: 'jak-zamowic-do-polski', icon: 'ri-ship-line' },
                  { label: 'Czy Kakobuy jest bezpieczny', slug: 'czy-kakobuy-jest-bezpieczny', icon: 'ri-shield-check-line' },
                  { label: 'Jak uniknąć oszustw', slug: 'jak-uniknac-oszustw', icon: 'ri-spy-line' },
                  { label: 'Bezpieczne płatności', slug: 'bezpieczne-platnosci-kakobuy', icon: 'ri-bank-card-line' },
                ].map((item) => (
                  <Link
                    key={item.slug}
                    to={`/${item.slug}`}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-600 rounded-xl hover:bg-background-100 font-label"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <div className="w-8 h-8 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-sm text-accent-500`}></i>
                    </div>
                    {item.label}
                  </Link>
                ))}
              </div>
            )}

            {/* CTA Button */}
            <div className="pt-3 pb-2">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="block text-center px-5 py-3 text-sm bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors duration-200 font-label font-medium whitespace-nowrap cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('btn_buy_now')}
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}